import React, { useState } from 'react';
import { useNavigate, Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { cn } from '../utils/cn';

export const DashboardLayout = () => {
  const { user, role, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  const displayName = user?.full_name ?? user?.name ?? '';

  const navLinks = {
    client: [
      { path: '/client/dashboard', label: 'My Documents', icon: FiIcons.FiFolder },
      { path: '/client/settings', label: 'Settings', icon: FiIcons.FiSettings },
    ],
    advisor: [
      { path: '/advisor/dashboard', label: 'My Clients', icon: FiIcons.FiUsers },
      { path: '/advisor/settings', label: 'Settings', icon: FiIcons.FiSettings },
    ],
    admin: [
      { path: '/admin/dashboard', label: 'Users', icon: FiIcons.FiUsers },
      { path: '/admin/vouchers', label: 'Vouchers', icon: FiIcons.FiTag },
    ],
  };

  const links = navLinks[role] || [];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row">
      {/* Sidebar Desktop */}
      <aside className="w-64 bg-brand-navy text-white hidden md:flex flex-col">
        <div className="p-6">
          <Link to="/" className="text-2xl font-bold tracking-tight text-white flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiBookOpen} className="text-brand-gold" />
            Libr Legacy
          </Link>
        </div>
        <nav className="flex-1 px-4 py-6 space-y-2">
          {links.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={cn(
                'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
                location.pathname.startsWith(link.path)
                  ? 'bg-slate-800 text-brand-gold'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              )}
            >
              <SafeIcon icon={link.icon} /> {link.label}
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 px-4 py-3 mb-2">
            <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center font-bold text-sm">
              {displayName?.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{displayName}</p>
              <p className="text-xs text-slate-400 capitalize">{role}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors">
            <SafeIcon icon={FiIcons.FiLogOut} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden bg-brand-navy text-white p-4 flex justify-between items-center">
        <Link to="/" className="text-xl font-bold flex items-center gap-2">
          <SafeIcon icon={FiIcons.FiBookOpen} className="text-brand-gold" />
          Libr Legacy
        </Link>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle navigation menu"
          aria-expanded={isMobileMenuOpen}
        >
          <SafeIcon icon={isMobileMenuOpen ? FiIcons.FiX : FiIcons.FiMenu} className="text-2xl" />
        </button>
      </div>

      {/* Mobile Drawer */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-brand-navy text-white border-t border-slate-800">
          <nav className="px-4 py-4 space-y-1">
            {links.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
                  location.pathname.startsWith(link.path)
                    ? 'bg-slate-800 text-brand-gold'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                )}
              >
                <SafeIcon icon={link.icon} /> {link.label}
              </Link>
            ))}
          </nav>
          <div className="px-4 pb-4 border-t border-slate-800 pt-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center font-bold text-sm">
                {displayName?.charAt(0)}
              </div>
              <div>
                <p className="text-sm font-medium">{displayName}</p>
                <p className="text-xs text-slate-400 capitalize">{role}</p>
              </div>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors">
              <SafeIcon icon={FiIcons.FiLogOut} /> Sign Out
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-5xl mx-auto">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
};